import axios, { AxiosError, AxiosInstance } from 'axios'

import { buildFactorialIdUrl } from 'modules/core/lib/buildUrl'

// Extend Axios request config to include retry flag
declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    _retry?: boolean
  }
}

export class FactorialIDApiError extends Error {
  public code: string
  public message: string

  constructor(
    code?: string,
    message?: string,
    public statusCode?: number,
    public originalError?: AxiosError
  ) {
    super(message)
    this.name = 'FactorialIDApiError'
    this.code = code || 'unknown_error'
    this.message = message || 'An unknown error occurred'
  }
}

let factorialIdAxios: AxiosInstance | null = null
let isRefreshing = false
let refreshPromise: Promise<void> | null = null

const refreshAccessToken = async (): Promise<void> => {
  const axiosInstance = axios.create({
    baseURL: buildFactorialIdUrl('/api'),
    timeout: 10000,
    withCredentials: true,
  })

  try {
    await axiosInstance.post('/auth/refresh')
  } catch (error) {
    // Refresh failed, redirect to login
    window.location.href = buildFactorialIdUrl('/login')
    throw error
  }
}

const getAxiosInstance = (): AxiosInstance => {
  if (!factorialIdAxios) {
    factorialIdAxios = axios.create({
      baseURL: buildFactorialIdUrl('/api'),
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    })

    factorialIdAxios.interceptors.response.use(
      (response) => {
        // Validate that successful responses contain valid JSON
        const contentType = response.headers['content-type']

        // Check if Content-Type indicates JSON
        if (contentType && !contentType.includes('application/json')) {
          throw new FactorialIDApiError(
            'invalid_response_type',
            `Expected JSON response but received ${contentType}`,
            response.status
          )
        }

        // Check if response data exists and is valid
        // Axios automatically parses JSON, so if we get a string when expecting
        // an object, or get null/undefined unexpectedly, the response wasn't valid JSON
        if (response.data === null || response.data === undefined) {
          throw new FactorialIDApiError(
            'empty_response',
            'Response body is empty or invalid',
            response.status
          )
        }

        return response
      },
      async (error: AxiosError) => {
        const originalRequest = error.config

        if (error.response) {
          const status = error.response.status
          const errorInfo = error.response.data?.error || {}

          // Handle 401 with token refresh
          if (
            status === 401 &&
            originalRequest &&
            !('_retry' in originalRequest && originalRequest._retry)
          ) {
            Object.assign(originalRequest, { _retry: true })

            try {
              // If a refresh is already in progress, wait for it
              if (isRefreshing && refreshPromise) {
                await refreshPromise
              } else {
                // Start a new refresh
                isRefreshing = true
                refreshPromise = refreshAccessToken()
                await refreshPromise
                isRefreshing = false
                refreshPromise = null
              }

              // Retry the original request with the new token
              return factorialIdAxios
                ? factorialIdAxios.request(originalRequest)
                : Promise.reject(new Error('Axios instance not initialized'))
            } catch (refreshError) {
              isRefreshing = false
              refreshPromise = null
              throw new FactorialIDApiError(errorInfo.code, errorInfo.message, status, error)
            }
          }

          switch (status) {
            case 401:
              throw new FactorialIDApiError(errorInfo.code, errorInfo.message, status, error)
            case 422:
              throw new FactorialIDApiError(
                errorInfo.code,
                errorInfo.message || 'Validation failed',
                status,
                error
              )
            case 500:
              throw new FactorialIDApiError(
                errorInfo.code,
                errorInfo.message || 'Internal server error',
                status,
                error
              )
            default:
              throw new FactorialIDApiError(
                errorInfo.code,
                errorInfo.message || `Request failed with status ${status}`,
                status,
                error
              )
          }
        } else if (error.request) {
          // Network error
          throw new FactorialIDApiError(undefined, undefined, undefined, error)
        } else {
          // Request setup error
          throw new FactorialIDApiError(undefined, undefined, undefined, error)
        }
      }
    )
  }

  return factorialIdAxios
}

// ============================================================================
// TOTP Endpoints
// ============================================================================
export interface TotpShowData {
  status: 'provisioned' | 'provisioning' | 'available' | 'unavailable'
  can_deprovision: boolean
  deprovision_requires_password: boolean
}

export interface TotpNewData {
  provisioning_uri: string
  secret: string
}

export async function getTotpStatus(): Promise<TotpShowData> {
  const response = await getAxiosInstance().get<TotpShowData>('/account/totp')
  return response.data
}

export async function startTotpProvisioning(): Promise<TotpNewData> {
  const response = await getAxiosInstance().get<TotpNewData>('/account/totp/new')
  return response.data
}

export async function confirmTotpProvisioning(otp: string): Promise<void> {
  await getAxiosInstance().post<void>('/account/totp', { otp })
}

export async function deprovisionTotp(password?: string): Promise<void> {
  const payload = password ? { password } : {}
  await getAxiosInstance().post<void>('/account/totp/delete', payload)
}

// ============================================================================
// Companies Endpoints
// ============================================================================
export interface CompanyData {
  id: string
  external_id: string
  cell: string
  name: string
  full_logo_url?: string
  fid_enabled: boolean
}

export async function getCompanies(): Promise<CompanyData[]> {
  const response = await getAxiosInstance().get<CompanyData[]>('/account/companies')
  return response.data
}

// ============================================================================
// Auth Endpoints
// ============================================================================

export interface AuthError {
  code: string
  message?: string
}

export interface CompanySelectionChallenge {
  type: 'company_selection'
}

export interface RedirectChallenge {
  type: 'redirect'
  to: string
}

export type AuthChallenge = CompanySelectionChallenge | RedirectChallenge

export interface AuthResponse {
  success: boolean
  error?: AuthError
  next?: AuthChallenge
}

export async function selectCompany(id: string): Promise<AuthResponse> {
  const response = await getAxiosInstance().post<AuthResponse>(
    '/auth/challenges/company_selection',
    { company_id: id }
  )
  return response.data
}

/**
 * Builds the Factorial ID login URL with challenge parameters
 * Similar to the Ruby implementation in login_routing.rb
 *
 * @param email - User's email
 * @param response - Auth response from Factorial ID
 * @returns Full URL to redirect to
 */
export const buildRedirectUrl = (email: string, response: AuthResponse): string => {
  const fidLoginUrl = buildFactorialIdUrl('/login')

  const params = new URLSearchParams()
  params.append('email', email)
  params.append('success', response.success.toString())

  if (response.next) {
    params.append('next.type', response.next.type)
  }

  if (response.error) {
    params.append('error.code', response.error.code)
    if (response.error.message) {
      params.append('error.message', response.error.message)
    }
  }

  return `${fidLoginUrl}?${params.toString()}`
}
